import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { HugeiconsIcon } from '@hugeicons/react';
import { 
  Mail01Icon, 
  ArrowRight01Icon,
  AccessIcon,
  ViewIcon,
  ViewOffSlashIcon,
  LockIcon
} from '@hugeicons/core-free-icons';
import { api } from '../utils/api';
import { FLAGS } from '../config/flags';

const ForgotPassword = () => {
  const [stage, setStage] = useState('request'); // 'request', 'verify', 'reset'
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  const navigate = useNavigate();

  const handleRequestSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      setError('The email field is required.');
      return;
    }
    setLoading(true);
    setError('');
    setSuccessMessage('');

    if (FLAGS.USE_MOCK_API) {
      await new Promise(resolve => setTimeout(resolve, 800));
      setSuccessMessage('A mock reset code has been sent to your email.');
      setStage('verify');
      setLoading(false);
      return;
    }

    try {
      const response = await api.post('/forgot-password', { email });
      setSuccessMessage(response.message || 'A reset code has been sent to your email.');
      setStage('verify');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifySubmit = async (e) => {
    e.preventDefault();
    if (!otp) {
      setError('Please enter the verification code.');
      return;
    }
    setLoading(true);
    setError('');
    setSuccessMessage('');

    if (FLAGS.USE_MOCK_API) {
      await new Promise(resolve => setTimeout(resolve, 800));
      setSuccessMessage('Code verified successfully.');
      setStage('reset');
      setLoading(false);
      return;
    }

    try {
      const response = await api.post('/verify-reset-code', { email, otp });
      setSuccessMessage(response.message || 'Code verified. You can now reset your password.');
      setStage('reset');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    if (password !== passwordConfirmation) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    setError('');
    setSuccessMessage('');

    if (FLAGS.USE_MOCK_API) {
      await new Promise(resolve => setTimeout(resolve, 800));
      setSuccessMessage('Password reset successfully! Redirecting...');
      setTimeout(() => navigate('/login'), 2000);
      setLoading(false);
      return;
    }

    try {
      const response = await api.post('/reset-password', { 
        email, 
        otp, 
        password, 
        password_confirmation: passwordConfirmation 
      });
      setSuccessMessage(response.message || 'Password has been reset successfully! Redirecting to login...');
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const renderRequestForm = () => (
    <>
      <div className="text-center">
        <h2 className="text-3xl font-bold text-charcoal dark:text-white">Forgot Password?</h2>
        <p className="mt-2 text-muted-gray">Don&apos;t worry! It happens. Enter your email and we&apos;ll send you a reset code.</p>
      </div>
      <form className="mt-8 space-y-6" onSubmit={handleRequestSubmit}>
        <div>
          <label className="block text-sm font-medium text-charcoal dark:text-gray-200 mb-1">Email Address</label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-muted-gray"><HugeiconsIcon icon={Mail01Icon} size={20} /></div>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="block w-full pl-10 pr-3 py-3 border border-light-gray/20 dark:border-gray-700/50 rounded-2xl focus:ring-soft-green focus:border-soft-green text-charcoal dark:text-white bg-off-white/50 dark:bg-gray-800/50" placeholder="name@company.com" />
          </div>
        </div>
        <button type="submit" disabled={loading} className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-bold rounded-3xl text-white bg-soft-green hover:bg-light-mint focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-soft-green transition-all duration-300 ease-in-out transform hover:-translate-y-1 hover:shadow-float disabled:opacity-50">
          {loading ? 'Sending...' : 'Send Reset Code'}
          <span className="absolute right-4 flex items-center"><HugeiconsIcon icon={ArrowRight01Icon} size={18} /></span>
        </button>
      </form>
    </>
  );

  const renderVerifyForm = () => (
    <>
      <div className="text-center">
        <h2 className="text-3xl font-bold text-charcoal dark:text-white">Enter Verification Code</h2>
        <p className="mt-2 text-muted-gray">A 6-digit code was sent to <span className="font-bold">{email}</span></p>
      </div>
      <form className="mt-8 space-y-6" onSubmit={handleVerifySubmit}>
        <div>
          <label className="block text-sm font-medium text-charcoal dark:text-gray-200 mb-1">Verification Code</label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-muted-gray"><HugeiconsIcon icon={LockIcon} size={20} /></div>
            <input type="text" value={otp} onChange={(e) => setOtp(e.target.value)} required className="block w-full pl-10 pr-3 py-3 border border-light-gray/20 dark:border-gray-700/50 rounded-2xl focus:ring-soft-green focus:border-soft-green text-charcoal dark:text-white bg-off-white/50 dark:bg-gray-800/50" placeholder="123456" />
          </div>
        </div>
        <button type="submit" disabled={loading} className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-bold rounded-3xl text-white bg-soft-green hover:bg-light-mint focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-soft-green transition-all duration-300 ease-in-out transform hover:-translate-y-1 hover:shadow-float disabled:opacity-50">
          {loading ? 'Verifying...' : 'Verify Code'}
          <span className="absolute right-4 flex items-center"><HugeiconsIcon icon={ArrowRight01Icon} size={18} /></span>
        </button>
      </form>
    </>
  );

  const renderResetForm = () => (
    <>
      <div className="text-center">
        <h2 className="text-3xl font-bold text-charcoal dark:text-white">Set New Password</h2>
        <p className="mt-2 text-muted-gray">Create a new secure password for your account.</p>
      </div>
      <form className="mt-8 space-y-6" onSubmit={handleResetSubmit}>
        <div>
          <label className="block text-sm font-medium text-charcoal dark:text-gray-200 mb-1">New Password</label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-muted-gray"><HugeiconsIcon icon={AccessIcon} size={20} /></div>
            <input type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} required className="block w-full pl-10 pr-10 py-3 border border-light-gray/20 dark:border-gray-700/50 rounded-2xl focus:ring-soft-green focus:border-soft-green text-charcoal dark:text-white bg-off-white/50 dark:bg-gray-800/50" placeholder="••••••••" />
            <button type="button" className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-gray" onClick={() => setShowPassword(!showPassword)}><HugeiconsIcon icon={showPassword ? ViewOffSlashIcon : ViewIcon} size={20} /></button>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-charcoal dark:text-gray-200 mb-1">Confirm New Password</label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-muted-gray"><HugeiconsIcon icon={AccessIcon} size={20} /></div>
            <input type={showPasswordConfirmation ? "text" : "password"} value={passwordConfirmation} onChange={(e) => setPasswordConfirmation(e.target.value)} required className="block w-full pl-10 pr-10 py-3 border border-light-gray/20 dark:border-gray-700/50 rounded-2xl focus:ring-soft-green focus:border-soft-green text-charcoal dark:text-white bg-off-white/50 dark:bg-gray-800/50" placeholder="••••••••" />
            <button type="button" className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-gray" onClick={() => setShowPasswordConfirmation(!showPasswordConfirmation)}><HugeiconsIcon icon={showPasswordConfirmation ? ViewOffSlashIcon : ViewIcon} size={20} /></button>
          </div>
        </div>
        <button type="submit" disabled={loading} className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-bold rounded-3xl text-white bg-soft-green hover:bg-light-mint focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-soft-green transition-all duration-300 ease-in-out transform hover:-translate-y-1 hover:shadow-float disabled:opacity-50">
          {loading ? 'Resetting...' : 'Reset Password'}
          <span className="absolute right-4 flex items-center"><HugeiconsIcon icon={ArrowRight01Icon} size={18} /></span>
        </button>
      </form>
    </>
  );

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full space-y-8 bg-white/80 dark:bg-dark-surface/80 backdrop-blur-md p-8 rounded-[2.5rem] border border-light-gray/20 dark:border-dark-border/20 shadow-float-lg">
        
        {stage === 'request' && renderRequestForm()}
        {stage === 'verify' && renderVerifyForm()}
        {stage === 'reset' && renderResetForm()}

        {(error || successMessage) && (
          <div className="mt-4">
            {error && <div className="bg-red-100 dark:bg-red-900/20 border border-red-200/50 text-red-700 dark:text-red-300 px-4 py-3 rounded-2xl text-sm">{error}</div>}
            {successMessage && <div className="bg-green-100 dark:bg-green-900/20 border border-green-200/50 text-green-700 dark:text-green-300 px-4 py-3 rounded-2xl text-sm">{successMessage}</div>}
          </div>
        )}

        <div className="text-center">
          <p className="text-sm text-muted-gray">
            Remember your password?{' '}
            <span className="font-bold text-soft-green hover:text-light-mint cursor-pointer" onClick={() => navigate('/login')}>
              Sign In
            </span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
